import { Body, Controller, Delete, Get, Param, Patch, Post, Req, UseGuards } from '@nestjs/common';
import { ApiOperation, ApiTags } from '@nestjs/swagger';
import { ShippingChannelName } from '@cart/shared-utils';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard, AuthenticatedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { StoreContextService } from '../tenancy/store-context.service';
import { QuoteRequest, ShippingService } from './shipping.service';

/**
 * Merchant-facing shipping configuration, plus the simulator.
 *
 * The public quote endpoint lives on StorefrontShippingController below — same
 * service, no guard, because a shopper needs a price before they have an
 * account.
 */
@ApiTags('shipping')
@Controller('admin/shipping')
@UseGuards(AuthGuard, AdminAccessGuard, DemoModeGuard)
export class ShippingAdminController {
  constructor(
    private readonly shipping: ShippingService,
    private readonly storeContext: StoreContextService
  ) {}

  private storeId(): string {
    return this.storeContext.storeId();
  }

  private actor(req: AuthenticatedRequest): string {
    return req.user?.id ?? 'system';
  }

  @Get()
  @ApiOperation({ summary: 'Shipping settings and both channels' })
  async config() {
    const storeId = this.storeId();
    const [settings, channels] = await Promise.all([
      this.shipping.getSettingsForAdmin(storeId),
      this.shipping.listChannels(storeId),
    ]);
    return { settings, channels };
  }

  @Patch('settings')
  @ApiOperation({ summary: 'Local/outstation boundary, pickup, default weight' })
  saveSettings(@Body() body: Record<string, unknown>, @Req() req: AuthenticatedRequest) {
    return this.shipping.saveSettings(this.storeId(), body, this.actor(req));
  }

  @Patch('channels/:channel')
  @ApiOperation({ summary: 'Set the one pricing method for a channel' })
  saveChannel(
    @Param('channel') channel: ShippingChannelName,
    @Body() body: Record<string, unknown>,
    @Req() req: AuthenticatedRequest
  ) {
    return this.shipping.saveChannel(this.storeId(), channel, body, this.actor(req));
  }

  @Delete()
  @ApiOperation({ summary: 'Reset shipping back to the default configuration' })
  reset(@Req() req: AuthenticatedRequest) {
    return this.shipping.resetAll(this.storeId(), this.actor(req));
  }

  @Delete('channels/:channel')
  @ApiOperation({ summary: 'Stop delivering on this channel' })
  deleteChannel(@Param('channel') channel: ShippingChannelName) {
    return this.shipping.deleteChannel(this.storeId(), channel);
  }

  /**
   * The simulator.
   *
   * Calls the same `quote()` checkout does, deliberately — a simulator with its
   * own pricing path would eventually disagree with the real one, and a
   * merchant would trust the wrong number.
   */
  @Post('simulate')
  @ApiOperation({ summary: 'Price a hypothetical order and show every step' })
  simulate(@Body() body: QuoteRequest) {
    return this.shipping.quote(this.storeId(), body);
  }
}

/**
 * Public quote. Unguarded and tenant-scoped like /store/config, because a
 * shopper needs delivery pricing before they have signed in.
 */
@ApiTags('shipping')
@Controller('store/shipping')
export class StorefrontShippingController {
  constructor(
    private readonly shipping: ShippingService,
    private readonly storeContext: StoreContextService
  ) {}

  @Post('quote')
  @ApiOperation({ summary: 'Delivery options and price for a cart and address' })
  quote(@Body() body: QuoteRequest) {
    return this.shipping.quote(this.storeContext.storeId(), body);
  }

  /**
   * City and state for a pincode.
   *
   * Autofill every Indian shopper now expects, and it comes free with the table
   * distance pricing already needs.
   */
  @Get('pincode/:pincode')
  @ApiOperation({ summary: 'Look up a pincode’s city and state' })
  pincode(@Param('pincode') pincode: string) {
    return this.shipping.lookupPincode(pincode);
  }
}
